<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.meeting_turns', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="md:grid md:grid-cols-4 md:gap-6">
            <div>
                <div class="mt-10 text-center lg:col-start-8 lg:col-end-13 lg:row-start-1 lg:mt-9 xl:col-start-9">
                    <div class="flex items-center text-gray-900">
                        <InertiaLink
                            :href="meta.links.index_previous_month"
                            class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
                        >
                            <span class="sr-only">Previous month</span>
                            <IconChevronLeft class="h-5 w-5" aria-hidden="true" />
                        </InertiaLink>
                        <div class="flex-auto text-sm font-semibold">{{ calendar.month_name }}</div>
                        <InertiaLink
                            :href="meta.links.index_next_month"
                            class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
                        >
                            <span class="sr-only">Next month</span>
                            <IconChevronRight class="h-5 w-5" aria-hidden="true" />
                        </InertiaLink>
                    </div>
                    <div class="mt-6 grid grid-cols-5 text-xs leading-6 text-gray-500">
                        <div>{{ $t('messages.monday_short') }}</div>
                        <div>{{ $t('messages.tuesday_short') }}</div>
                        <div>{{ $t('messages.wednesday_short') }}</div>
                        <div>{{ $t('messages.thursday_short') }}</div>
                        <div>{{ $t('messages.friday_short') }}</div>
                    </div>
                    <div
                        class="isolate mt-2 grid grid-cols-5 gap-px rounded-lg bg-gray-200 text-sm shadow ring-1 ring-gray-200"
                    >
                        <InertiaLink
                            v-for="(day, dayIdx) in calendar.days"
                            :key="day.date"
                            type="button"
                            :href="day.links.index"
                            :class="[
                                'py-1.5 hover:bg-gray-100 focus:z-10',
                                !day.is_current_month && day.count === 0 ? 'bg-gray-50' : '',
                                !day.is_current_month && day.count > 0 ? 'bg-gray-100' : '',
                                day.is_current_month && day.count === 0 ? 'bg-white' : '',
                                day.is_current_month && day.count > 0 ? 'bg-indigo-100' : '',
                                (day.is_selected || day.is_today) && 'font-semibold',
                                day.is_selected && 'text-white',
                                !day.is_selected && day.is_current_month && !day.is_today && 'text-gray-900',
                                !day.is_selected && !day.is_current_month && !day.is_today && 'text-gray-400',
                                day.is_today && !day.is_selected && 'text-indigo-600',
                                dayIdx === 0 && 'rounded-tl-lg',
                                dayIdx === 4 && 'rounded-tr-lg',
                                dayIdx === calendar.days.length - 5 && 'rounded-bl-lg',
                                dayIdx === calendar.days.length - 1 && 'rounded-br-lg',
                            ]"
                        >
                            <time
                                :datetime="day.date"
                                :class="[
                                    'mx-auto flex h-7 w-7 items-center justify-center rounded-full',
                                    day.is_selected && day.is_today && 'bg-indigo-600',
                                    day.is_selected && !day.is_today && 'bg-gray-900',
                                ]"
                                >{{ day.date.split('-').pop().replace(/^0/, '') }}</time
                            >
                        </InertiaLink>
                    </div>
                </div>
            </div>

            <div class="col-span-3">
                <div class="font-semibold">
                    {{ meta.info.date_display }}
                </div>
                <div v-if="turns.length === 0" class="flex flex-col mt-5">
                    <EmptyState picture />
                </div>
                <div v-else>
                    <ol class="mt-2">
                        <li v-for="turn in turns" :key="turn.id">
                            <button
                                class="sm:flex justify-between group overflow-y-auto rounded-lg p-2 text-xs leading-5 mt-1 w-full"
                                :class="{
                                    'bg-blue-50 hover:bg-blue-100': turn.status_color === 'blue',
                                    'bg-green-50 hover:bg-green-100': turn.status_color === 'green',
                                    'bg-gray-50 hover:bg-gray-100': turn.status_color === 'gray',
                                }"
                                @click="openTurn(turn)"
                            >
                                <div class="flex items-center gap-x-2">
                                    <span
                                        class="truncate font-medium text-gray-900 group-hover:text-indigo-600 text-left"
                                    >
                                        {{ turn.group.name }}
                                    </span>
                                    <span v-if="turn.reserved_by_user">
                                        <IconCalendarCheck class="ml-1 h-4 w-4 text-blue-400" />
                                    </span>
                                </div>
                                <time :datetime="turn.datetime" class="ml-3 flex-none text-gray-500">
                                    {{ turn.time_start }} - {{ turn.time_end }}
                                </time>
                            </button>
                        </li>
                    </ol>
                </div>
            </div>
        </div>
        <RequestMeetingTurnSlideOver
            :turn="turn"
            :students="students"
            :options="options"
            @close="turn = null"
            v-if="turn !== null"
        />
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import EmptyState from '@/components/EmptyState.vue';
import { IconCalendarCheck, IconChevronLeft, IconChevronRight } from '@tabler/icons-vue';
import RequestMeetingTurnSlideOver from '@/Pages/Relative/MeetingTurns/Partials/RequestMeetingTurnSlideOver.vue';

export default {
    components: {
        RequestMeetingTurnSlideOver,
        PanelPage,
        EmptyState,
        IconChevronLeft,
        IconChevronRight,
        IconCalendarCheck,
    },
    layout: Layout,
    name: 'Index',
    props: ['meta', 'calendar', 'filters', 'options', 'turns'],
    data() {
        return {
            form: this.$inertia.form({
                group_ids: this.filters.group_ids,
                day_month: this.filters.day_month,
                date: this.filters.date,
            }),
            slideOverOpen: false,
            turn: null,
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
        openTurn(turn) {
            this.slideOverOpen = true;
            this.turn = turn;
        },
    },
    computed: {
        students: function () {
            if (this.turn === null) {
                return [];
            }

            return this.options.students.filter((s) => s.group_id == this.turn.group_id);
        },
    },
};
</script>
